import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}
interface State {
  hasError: boolean;
  message?: string;
}

/**
 * 全域錯誤邊界：捕捉渲染期間的非預期錯誤，避免整頁白屏。
 * 保留錯誤訊息以利偵錯（正式環境會顯示簡短提示）。
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? error.message : "未知錯誤";
    return { hasError: true, message };
  }

  componentDidCatch(error: unknown, info: unknown) {
    console.error("App crashed:", error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, message: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="min-h-dvh flex flex-col items-center justify-center px-[max(22px,4.5vw)] text-center">
            <div className="text-[20px] font-bold text-ink mb-[8px]">發生錯誤</div>
            <p className="text-[14px] text-ink4 mb-[20px] max-w-[320px]">
              應用程式遇到未預期的問題。你可以重試或重新整理頁面。
            </p>
            <button
              type="button"
              onClick={this.handleReset}
              className="rounded-full px-6 py-3 bg-ink text-cream text-[15px] font-bold cursor-pointer"
            >
              重試
            </button>
            {this.state.message && (
              <pre className="mt-[16px] text-[12px] text-ink4 whitespace-pre-wrap max-w-[320px] text-left">
                {this.state.message}
              </pre>
            )}
          </div>
        )
      );
    }
    return this.props.children;
  }
}
